import { useMemo } from 'react'

import { sortArrayBy } from '@domain-group/javascript-utils'

import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CampaignSaleMethodRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['campaignSaleMethods']

type UseCampaignSaleMethodsOptions = {
  performHttpRequests?: boolean,
  requestOptions?: CampaignSaleMethodRequestOptions,
}

function useCampaignSaleMethods(options: UseCampaignSaleMethodsOptions) {
  const { performHttpRequests = false, requestOptions } = options

  const {
    updatedEntities: { campaignSaleMethods: campaignSaleMethodsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaignSaleMethods } = useSelector(reduxState => reduxState.entities)

  const filteredCampaignSaleMethods = useMemo(
    () => sortArrayBy(Object.values(campaignSaleMethods), 'asc', 'title'),
    [campaignSaleMethodsUpdatedAt],
  )

  const filteredCampaignSaleMethodsCount = filteredCampaignSaleMethods.length
  const hasFilteredCampaignSaleMethods = !!filteredCampaignSaleMethodsCount

  const { loading: loadingCampaignSaleMethods } = useReduxAction(
    'campaignSaleMethods',
    'loadCampaignSaleMethods',
    {
      ...requestOptions,
    },
    [performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    filteredCampaignSaleMethods,
    filteredCampaignSaleMethodsCount,
    hasFilteredCampaignSaleMethods,
    loading: loadingCampaignSaleMethods,
  }
}

export default useCampaignSaleMethods
